import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Typography from '@mui/material/Typography';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import { TimePicker } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
// animation用のcss（antdのをそのまま使うとglobal cssが汚染されてしまうため）
import '~/assets/styles/timepicker.css';

import RequiredLabel from '@ui-elements/RequiredLabel';

import { DELIVERY_TIME } from '~/constants';
import { FONT_SIZES, PALETTE } from '~/assets/styles/base';

dayjs.extend(customParseFormat);

const TimePickerWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

type StyledLabelProps = {
  $label: string;
};

const StyledLabel = styled(Typography)<StyledLabelProps>`
  width: 70px;
  font-size: ${FONT_SIZES.bodyDefault};
  text-align: left;
  line-height: 40px;
`;

type StyledTimePickerProps = {
  $width: string;
};

/**
 * selectboxのcssはグローバルになるのでsrc/assets/styles/antd.jsで指定している
 * */
const StyledTimePicker = styled(TimePicker)<StyledTimePickerProps>`
  width: ${({ $width }) => $width};
  border-radius: 4px;
  .ant-picker {
    border: 1px solid ${PALETTE.gray[200]};
  }
  &.ant-picker.ant-picker-disabled {
    cursor: default;
    pointer-events: none;
  }
  & .ant-picker-input input {
    font-size: ${FONT_SIZES.bodyDefault};
    color: ${PALETTE.text.main};
  }
  & .ant-picker:hover,
  & .ant-picker-focused {
    border-color: none;
    border-right-width: 1px;
  }

  .ant-picker-focused {
    border-color: none;
    box-shadow: 0 0 0 0;
  }
`;

const TimePickerFooter = styled.div`
  display: grid;
  grid-auto-flow: column;
  line-height: 22px;

  & .ant-picker-footer-extra {
    padding-left: 7px;
  }
  & div {
    padding-left: 14px;
    text-align: left;
  }
`;

const Icon = styled(AccessTimeIcon)`
  font-size: ${FONT_SIZES.body5};
  color: ${PALETTE.gray[600]};
`;

type Props = {
  label: string;
  defTime?: string;
  value?: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  handleChange: Function;
  format?: string;
  minStep?: number;
  showNow?: boolean;
  allowClear?: boolean;
  width?: string;
  disabled?: boolean;
  required?: boolean;
  dataTestId?: string;
};

/**
 * UIデザインの問題でここだけantdを使用している。
 * @see https://ant.design/components/time-picker/
 *
 * カバレッジignoreしている箇所は、antdのTimePickerをモックしないとjestでエラーが出てしまい通すことができないため。
 */
const MyTimePicker: React.FC<Props> = ({
  label,
  defTime,
  handleChange,
  value = undefined,
  format = DELIVERY_TIME.FORMAT,
  minStep = DELIVERY_TIME.MIN_STEP,
  showNow = false,
  allowClear = false,
  width = '210px',
  disabled = false,
  required = false,
  dataTestId = 'uiParts-TimePicker',
}): JSX.Element => {
  const { t } = useTranslation();

  return (
    <TimePickerWrapper data-testid="uiParts-TimePickerWrapper">
      {required && label ? (
        <StyledLabel $label={label} data-testid="uiParts-TimePicker-lbl-req">
          <RequiredLabel />
          {label}
        </StyledLabel>
      ) : (
        <StyledLabel $label={label} data-testid="uiParts-TimePicker-lbl-opt">
          {label}
        </StyledLabel>
      )}
      <StyledTimePicker
        onChange={
          /* istanbul ignore next */
          (time, timeString) => handleChange(time, timeString)
        }
        defaultValue={dayjs(defTime, format)}
        value={value ? dayjs(value, format) : undefined}
        format={format}
        minuteStep={minStep}
        showNow={showNow}
        allowClear={allowClear}
        suffixIcon={<Icon />}
        renderExtraFooter={
          /* istanbul ignore next */ () => (
            <TimePickerFooter>
              <div>
                <Typography variant="note1">{t('appCommon.hourSuffix')}</Typography>
              </div>
              <div>
                <Typography variant="note1">{t('appCommon.minuteSuffix')}</Typography>
              </div>
            </TimePickerFooter>
          )
        }
        $width={width}
        data-testid={dataTestId}
        disabled={disabled}
        inputReadOnly
      />
    </TimePickerWrapper>
  );
};

export default MyTimePicker;
